import { Button, Dialog } from '@today/ui-primitives'
import { type ReactElement, useRef } from 'react'
import { ErrorText, FocusTextArea, useFocusOnOpen } from './content-fields'
import type { Translate } from './content-types'

/**
 * Props for {@link ReasonDialogView} — reject, pull back and retire.
 *
 * @property reasonError - Translated message under the reason field.
 * @property error - A server refusal shown at the top of the still-open dialog.
 */
export interface ReasonDialogViewProps {
	t: Translate
	isOpen: boolean
	title: string
	confirmLabel: string
	reason: string
	reasonError?: string
	error?: string | null
	isBusy?: boolean
	onReasonChange: (reason: string) => void
	onConfirm: () => void
	onCancel: () => void
}

/** A dialog that asks for the required reason of a review action (organism). Focus starts on the reason. */
export function ReasonDialogView(props: ReasonDialogViewProps): ReactElement {
	const { t } = props
	const reasonRef = useRef<HTMLTextAreaElement>(null)
	useFocusOnOpen(reasonRef, props.isOpen)
	return (
		<Dialog
			isOpen={props.isOpen}
			onOpenChange={(open) => (open ? undefined : props.onCancel())}
			title={props.title}
			labels={{ close: t('actions.close') }}
		>
			<div className="flex flex-col gap-3">
				<ErrorText message={props.error} />
				<FocusTextArea
					label={t('review.reason')}
					value={props.reason}
					error={props.reasonError}
					inputRef={reasonRef}
					onChange={props.onReasonChange}
				/>
				<div className="flex gap-2">
					<Button isDisabled={props.isBusy} onPress={props.onConfirm}>
						{props.confirmLabel}
					</Button>
					<Button variant="muted" onPress={props.onCancel}>
						{t('actions.cancel')}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}
